import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  cancelAnalysis,
  getAnalysis,
  recordReportView,
  isIngestionResult,
  pinGoverningProforma,
  type AnalysisRun,
  type AnalysisResult,
  type ExcludedDocument,
  type InvestmentScore,
  type PartialDocument,
  type ExtractionReadLimit,
  type ExtractionSummary,
  type RecordPin,
  type ProformaSpineRead,
  type SpineOverrideEntry,
  type IngestionResult,
  type LandBasis,
  type PropertyTax,
  type RecordAsOf,
  type CapRateSpread,
  type ExitCapReferenceView,
  type SiteContext,
  type SiteIdentity,
  type SponsorFilings,
  type Reconciliation,
} from '../api.js';
import { AppHeader } from '../components/AppHeader.js';
import { ContactConsentRecord } from '../components/ContactConsent.js';
import { BackLink } from '../components/BackLink.js';
import { AnalysisReport } from '../components/AnalysisReport.js';
import { ProgramTypeNote } from '../components/ProgramTypeNote.js';
import { ReportChanges } from '../components/ReportChanges.js';
import {
  DealIdentity,
  hasDealIdentity,
  type DealIdentityFields,
} from '../components/DealIdentity.js';
import { useChallenges, type ChallengeContext } from '../components/Challenges.js';
import { useSession } from '../useSession.js';
import { canSubmitAnalysis } from '../roles.js';
import { currency, currencyCompact, percentFromRatio, timestamp } from '../format.js';
import { signInPathFor } from '../return-path.js';
import { progressSteps } from '../run-progress.js';

const POLL_MS = 2500;
const MAX_POLLS = 160; // ~6.5 min ceiling before we stop and offer a manual refresh.

type ViewState =
  | { status: 'loading' }
  | { status: 'polling'; run: AnalysisRun }
  | { status: 'done'; run: AnalysisRun }
  | { status: 'error'; message: string }
  | { status: 'canceled' }
  | { status: 'timeout' };

export function AnalysisView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const session = useSession();
  const [view, setView] = useState<ViewState>({ status: 'loading' });
  const [canceling, setCanceling] = useState(false);
  const pollsRef = useRef(0);
  // One engagement row per report opened — see the beacon in `tick`.
  const viewRecordedRef = useRef(false);

  // Challenges are only meaningful once there IS a conclusion, so the ledger loads for a finished run
  // and not before. `role` rides the session claims; anything other than owner/admin can file but not
  // answer — and the server enforces that independently, this only decides what to draw.
  const role = session.status === 'authed' ? session.claims.role : undefined;
  const canAnswer = role === 'owner' || role === 'admin';
  /*
    Whether to OFFER the two write actions on this page — "Stop this run" and the "New analysis"
    link, both of which a viewer cannot use (`#768` gates cancel; the link lands on a page that now
    redirects them straight back). Same rule as the page they would have arrived from, read from one
    place so the two screens cannot drift.

    ⚠️ Drawing only. `POST /analyses/:id/cancel` refuses a viewer regardless — see `roles.ts`.
  */
  const canSubmit = session.status === 'authed' && canSubmitAnalysis(session.claims);
  const challenge = useChallenges(view.status === 'done' && id ? id : null, canAnswer);

  useEffect(() => {
    if (!id || session.status !== 'authed') return;
    const analysisId = id;
    let active = true;
    let timer: ReturnType<typeof setTimeout> | undefined;

    async function tick() {
      try {
        const run = await getAnalysis(analysisId);
        if (!active) return;
        if (!run) {
          void navigate(signInPathFor(window.location, window.location.origin), { replace: true });
          return;
        }
        if (run.status === 'succeeded') {
          setView({ status: 'done', run });
          /*
            ⚠️ THE ENGAGEMENT BEACON, and the `if` is the whole correctness of it. This route is
            POLLED every 2.5s while a run is in flight; recording on every response would count one
            reading as dozens of opens and make the engagement figure meaningless. It fires on the
            transition to a finished report — once per mount, guarded by a ref so a re-render or a
            second tick cannot double it.

            Not awaited, deliberately: the reader's report must not wait on, or fail with, a usage row.
          */
          if (!viewRecordedRef.current) {
            viewRecordedRef.current = true;
            void recordReportView(analysisId);
          }
          return;
        }
        if (run.status === 'failed') {
          // Colleague register (UI voice R5): say what happened + a next step, without surfacing the
          // backend's raw `run.error` string (an internal detail). The failed run is kept on the record.
          setView({ status: 'error', message: failedRunMessage(run) });
          return;
        }
        if (run.status === 'canceled') {
          setView({ status: 'canceled' });
          return;
        }
        setView({ status: 'polling', run });
        pollsRef.current += 1;
        if (pollsRef.current >= MAX_POLLS) {
          setView({ status: 'timeout' });
          return;
        }
        timer = setTimeout(() => void tick(), POLL_MS);
      } catch (err) {
        if (!active) return;
        setView({
          status: 'error',
          message: err instanceof Error ? err.message : 'Unexpected error.',
        });
      }
    }

    void tick();
    return () => {
      active = false;
      if (timer) clearTimeout(timer);
    };
  }, [id, session.status, navigate]);

  async function handleStop() {
    if (!id || canceling) return;
    setCanceling(true);
    try {
      await cancelAnalysis(id);
      // Reflect it immediately; the poll loop would also converge here on its next tick.
      setView({ status: 'canceled' });
    } catch {
      // Cancel failed (offline / 5xx) — stay on the run so the user can retry; it may still finish.
      setCanceling(false);
    }
  }

  if (session.status !== 'authed') {
    return <div className="app__loading">Loading…</div>;
  }

  return (
    <div className="app">
      <AppHeader claims={session.claims} />
      <main className="app__main">
        <div className="app__crumbs">
          {/* Back, not "start over". A reader who came from a project was previously sent to the
              deal-entry form, which is a different action wearing a back arrow.
              The fallback is the DEALS list — a deep-linked report (a pasted URL, a fresh tab) has no
              history to pop, and its natural parent is the list of deals, not the list of projects.
              That page did not exist when this fallback was chosen; `/projects` was the closest thing
              to a parent the app had. */}
          <BackLink fallback="/analyses" label="All deals" />
          {canSubmit && (
            <button type="button" className="link-btn" onClick={() => void navigate('/')}>
              New analysis
            </button>
          )}
          {/* Stop lives here (top of the run) so it's visible without scrolling past the stepper — an
              in-flight run can always be halted from a consistent, discoverable spot. */}
          {canSubmit && (view.status === 'loading' || view.status === 'polling') && (
            <button
              type="button"
              className="progress__stop"
              onClick={() => void handleStop()}
              disabled={canceling}
            >
              {canceling ? 'Stopping…' : 'Stop this run'}
            </button>
          )}
        </div>

        {(view.status === 'loading' || view.status === 'polling') && (
          <Progress run={view.status === 'polling' ? view.run : undefined} />
        )}

        {view.status === 'canceled' && (
          <div className="notice">
            <strong>Analysis canceled.</strong> This run was stopped before it finished.{' '}
            <button type="button" className="link-btn" onClick={() => void navigate('/')}>
              Start a new analysis
            </button>
            .
          </div>
        )}

        {view.status === 'timeout' && (
          <div className="notice">
            This is taking longer than expected. The run is still processing — you can keep this
            page open and{' '}
            <button type="button" className="link-btn" onClick={() => window.location.reload()}>
              refresh
            </button>
            .
          </div>
        )}

        {view.status === 'error' && (
          <div className="notice error">
            <strong>Analysis failed.</strong> {view.message}
          </div>
        )}

        {view.status === 'done' && (
          <>
            <p className="app__stamp">Completed {timestamp(view.run.completedAt)}</p>
            {/*
              What the client answered about being contacted (Ground Truth §2.4, #569). Like identity,
              it rides the RUN ROW rather than the analysis bundle — it is a fact about the submission,
              not about the underwrite, and it is deliberately outside `DoneReport` for that reason.
              Always rendered: "never asked" is a real state canon needs stated, so there is no
              condition under which this block is silent.
            */}
            {/*
              R8 currency, ABOVE the report rather than beside it. A reader who has already read the
              numbers has been misled; the notice has to arrive before they do. This is the promise
              CLAUDE.md calls the product's core — never work from a superseded drawing — reaching
              the page a client is actually sent to.
            */}
            <ReportCurrency
              supersededBy={view.run.supersededBy}
              projectCode={view.run.project?.code ?? null}
              producedUnderwrite={
                !(
                  isIngestionResult(view.run.result) &&
                  view.run.result.outcome === 'needs_more_docs'
                )
              }
            />
            {/*
              R1 — the question a returning client actually arrives with. Directly under the currency
              notice and above the report, in the same order the reader needs them: whether this is
              the current version, then what moved to produce it, then the underwrite itself.
            */}
            <ReportChanges
              previous={view.run.previousUnderwrite}
              current={view.run.result}
              projectCode={view.run.project?.code ?? null}
            />
            <ContactConsentRecord
              state={view.run.contactConsent ?? null}
              at={view.run.contactConsentAt ?? null}
            />
            {/* Identity rides the RUN ROW, not the analysis bundle — see `AnalysisReport`'s prop. */}
            <DoneReport
              result={view.run.result}
              challenge={challenge}
              identity={{
                site: view.run.site ?? null,
                sponsorName: view.run.sponsorName ?? null,
                project: view.run.project ?? null,
              }}
              // ADR-0069 N32 — offered only to the OWNER (`basis:lock`, Brad 2026-10-08; the other
              // deal-fact pins take owner/admin), and only for a run filed under a project. The server
              // refuses anyone else regardless.
              {...(mayPinGoverningProforma(role) && view.run.project?.code
                ? { pinProjectCode: view.run.project.code }
                : {})}
            />
          </>
        )}
      </main>
    </div>
  );
}

/**
 * "You are reading a superseded underwrite" — R8's currency guarantee on the report itself.
 *
 * ⚠️ THREE STATES, AND SILENCE IS ONLY CORRECT FOR ONE OF THEM. An UNBOUND run (no project code)
 * cannot have a successor, so the question was never asked and the component says nothing — stating
 * "this is current" there would be a clean produced by the absence of the check rather than by the
 * check passing. A BOUND run with no successor is genuinely current and says so quietly. A bound run
 * WITH a successor says so loudly and links to it.
 *
 * The same absent-versus-negative discipline as `CurrencyBadge` on the project page, for the same
 * reason: a reader cannot tell "checked and fine" from "never checked" unless we say which.
 */
export function ReportCurrency({
  supersededBy,
  projectCode,
  producedUnderwrite = true,
}: {
  supersededBy?: string;
  projectCode: string | null;
  /**
   * False for a run that stopped for more documents (`needs_more_docs`): stored `succeeded`, but it
   * produced no underwrite. ⚠️ #1394 made record-started runs resolve to their project, so a thin
   * connector run now HAS a project code — and "this is the latest underwrite" would be a claim about
   * an underwrite that does not exist (Bugbot, #1402). The list withholds `superseded` on the same rows.
   */
  producedUnderwrite?: boolean;
}) {
  const navigate = useNavigate();
  // Never asked — an unbound run has nothing to be superseded BY.
  if (!projectCode) return null;
  // A run with no underwrite is not "the latest underwrite"; the stopped-run card says what it is.
  if (!producedUnderwrite && !supersededBy) return null;
  if (!supersededBy) {
    return (
      <p className="report-currency report-currency--current">
        This is the latest underwrite for {projectCode}.
      </p>
    );
  }
  return (
    <div className="report-currency report-currency--stale" role="status">
      <span>
        ⚠ A newer underwrite of {projectCode} has been published since this one.
        {producedUnderwrite ? ' The figures below are from the earlier run.' : ''}
      </span>{' '}
      <button
        type="button"
        className="link-btn"
        onClick={() => void navigate(`/analyses/${supersededBy}`)}
      >
        Open the latest
      </button>
    </div>
  );
}

/** Friendly labels for the run-blocking extraction fields surfaced in a `needs_more_docs` outcome. */
const GAP_LABELS: Record<string, string> = {
  noi: 'Net operating income (NOI)',
  totalCost: 'Total development cost',
  loanAmount: 'Loan amount',
  annualDebtService: 'Annual debt service',
  exitCapRate: 'Exit cap rate',
  totalUnits: 'Unit count',
  potentialBaseRent: 'Potential base rent',
  totalOpex: 'Operating expenses',
  isLIHTC: 'Program type (LIHTC vs market-rate)',
  // The proforma spine's fields (ADR-0042). Added when the override got a reader: the panel names
  // the figures the workbook moved, and `holdYears` / `netRentableSF` on a client's page is our
  // schema leaking. Shared with the gap and provenance lists, which read better for it too.
  unitSF: 'Average unit size',
  netRentableSF: 'Net rentable area',
  avgRent: 'Average rent',
  rentPSF: 'Rent per square foot (monthly)',
  miscIncome: 'Miscellaneous income',
  hardCosts: 'Hard costs',
  softCosts: 'Soft costs',
  landCost: 'Land cost',
  equity: 'Equity',
  loanRate: 'Loan rate',
  holdYears: 'Hold period (years)',
  constructionMonths: 'Construction period (months)',
  constructionLoanAmount: 'Construction loan',
  permanentLoanAmount: 'Permanent loan',
  propertyClass: 'Property class',
  replacementReserves: 'Replacement reserves',
  vacancyRate: 'Vacancy rate',
  salePrice: 'Sale price',
  expenseRatio: 'Expense ratio',
};
const gapLabel = (k: string): string => GAP_LABELS[k] ?? k;

/** Render a completed run — a manual-deal bundle, or an ingestion wrapper (analyzed / needs more docs). */
function DoneReport({
  result,
  challenge,
  identity,
  pinProjectCode,
}: {
  result: AnalysisResult | null;
  challenge: ChallengeContext | null;
  identity?: DealIdentityFields;
  /** The project a governing-proforma pin would be written to — set only for those who may pin. */
  pinProjectCode?: string;
}) {
  if (!result) return <div className="notice">The run completed but returned no result.</div>;
  if (!isIngestionResult(result))
    return <AnalysisReport run={result} challenge={challenge} identity={identity} />;
  const excluded = result.excludedDocuments ?? [];
  const partial = result.partialDocuments ?? [];
  return (
    <>
      {result.reviewState === 'pending_review' && (
        <section className="notice warn">
          <strong>Pending review.</strong> This deal was underwritten automatically from documents
          that arrived on their own — it is held for a human to review and has <em>not</em> been
          released to the client.
        </section>
      )}
      {result.outcome === 'needs_more_docs' ? (
        <>
          {/* This page has no report to title, which is exactly why it needs the header: "we could
              not underwrite it" is only actionable once you know WHICH package it was said about. */}
          {identity && hasDealIdentity(identity) && <DealIdentity fields={identity} />}
          {excluded.length > 0 && <UnreadSources excluded={excluded} />}
          {partial.length > 0 && <PartiallyReadSources partial={partial} />}
          <ExtractionReadLimits limits={readLimitsOf(result)} />
          <NeedsMoreDocs result={result} />
          {/*
            ADR-0069 N32 (#1395) — the governing-proforma question, on the page where it most often
            STOPS the run: rival templates disagree → no workbook figure applied → NOI missing → here.
            ⚠️ Until this was drawn, the one surface the pin exists for had no button, and on 2025-014
            the owner's pin was written from the browser console. The worker persists the spine read on
            this outcome too (`extraction.spine`), so the page has what it needs.
          */}
          <StoppedRunGoverningProforma
            spine={result.extraction?.spine}
            {...(pinProjectCode ? { pinProjectCode } : {})}
          />
        </>
      ) : result.analysis ? (
        // Provenance rides INSIDE the report's Sources section rather than stacking above it: what the
        // documents said and how cleanly they read is part of the deal, not a preamble to it.
        <AnalysisReport
          run={result.analysis}
          challenge={challenge}
          identity={identity}
          budgetOmissions={result.constructionBudget?.omissions?.entries ?? null}
          notChecked={notCheckedFrom(result)}
          sources={
            <>
              {result.extraction && (
                <ExtractionBanner
                  extraction={result.extraction}
                  docCount={result.documentIds?.length ?? 0}
                  {...(pinProjectCode ? { pinProjectCode } : {})}
                />
              )}
              {result.extraction && (
                <ProgramTypeAssumption
                  extraction={result.extraction}
                  score={result.analysis.scorecard?.score ?? null}
                />
              )}
              {result.extraction?.programTypeNote && (
                <ProgramTypeNote note={result.extraction.programTypeNote} />
              )}
              {result.reconciliation && result.reconciliation.findings.length > 0 && (
                <SourceAgreement reconciliation={result.reconciliation} />
              )}
              {/* FIRST among the external panels: it says which parcel the rest of them are about. */}
              {identity?.site && <ParcelIdentityPanel site={identity.site} />}
              {result.landBasis && <LandBasisPanel landBasis={result.landBasis} />}
              {result.propertyTax && <PropertyTaxPanel propertyTax={result.propertyTax} />}
              {result.capRateSpread && <CapRateSpreadPanel capRateSpread={result.capRateSpread} />}
              {result.analysis.deterministic?.exitCapReference && (
                <ExitCapReferencePanel reference={result.analysis.deterministic.exitCapReference} />
              )}
              {result.siteContext && <SiteContextPanel siteContext={result.siteContext} />}
              {result.sponsorFilings && (
                <SponsorFilingsPanel sponsorFilings={result.sponsorFilings} />
              )}
              {result.externalChecksUnkeyed && result.externalChecksUnkeyed.length > 0 && (
                <ExternalChecksUnkeyed unkeyed={result.externalChecksUnkeyed} />
              )}
              {result.externalCheckFailures && result.externalCheckFailures.length > 0 && (
                <ExternalCheckFailures failed={result.externalCheckFailures} />
              )}
              {excluded.length > 0 && <UnreadSources excluded={excluded} />}
              {partial.length > 0 && <PartiallyReadSources partial={partial} />}
              <ExtractionReadLimits limits={readLimitsOf(result)} />
            </>
          }
        />
      ) : (
        <>
          {excluded.length > 0 && <UnreadSources excluded={excluded} />}
          {partial.length > 0 && <PartiallyReadSources partial={partial} />}
          <ExtractionReadLimits limits={readLimitsOf(result)} />
          <div className="notice">The documents were processed but no analysis was produced.</div>
        </>
      )}
    </>
  );
}

/**
 * What a FAILED run says to its reader. Colleague register (UI voice R5): what happened and a next step,
 * without the backend's raw `run.error` — with ONE exception, keyed on the code, never on the text.
 *
 * #1421 — `documents_withdrawn` is the run REFUSED because every document it could read was withdrawn
 * from the project record. "Try again shortly" is false advice there: a retry reads the same set. Its
 * stored message is a sentence written for a reader by the worker that refused, so it is shown. Every
 * other code keeps the generic line — `no_documents` included, which is a failed ingest a retry CAN
 * fix — so this does not widen what error text is shown.
 */
function failedRunMessage(run: Pick<AnalysisRun, 'error' | 'errorCode'>): string {
  if (
    run.errorCode === 'documents_withdrawn' &&
    typeof run.error === 'string' &&
    run.error.trim()
  ) {
    const said = run.error.trim();
    return `This analysis could not run: ${said}${/[.!?]$/.test(said) ? '' : '.'}`;
  }
  return 'This analysis didn’t finish. You can start a new run, or try again shortly.';
}

/**
 * Data-quality honesty banner: some source documents couldn't contribute — rejected at ingest (an
 * unsupported file type), or indexed with no readable text (a scanned drawing). They did NOT feed the
 * underwrite. We surface exactly which, so a partial read is never silently scored as a complete package.
 */
const EXCLUSION_REASONS: Record<ExcludedDocument['reason'], string> = {
  no_text: 'no readable text (scanned or image-only)',
  index_failed: 'failed to index',
  index_timeout: 'still processing — indexing timed out',
  unsupported_type: 'unsupported file type',
  ingest_failed: 'could not be ingested',
  not_found: 'no longer available in the document store',
  withdrawn: 'withdrawn from the project record — not read',
};

function UnreadSources({ excluded }: { excluded: ExcludedDocument[] }) {
  return (
    <section className="notice warn unread">
      <strong>
        {excluded.length} source document{excluded.length === 1 ? '' : 's'} could not be used
      </strong>{' '}
      and did not feed this analysis — the underwrite reflects only the documents we could read.
      <ul className="unread__list">
        {excluded.map((d, i) => (
          <li key={d.documentId ?? `${d.filename ?? 'doc'}-${i}`}>
            {d.filename ?? d.documentId ?? 'A document'} —{' '}
            {EXCLUSION_REASONS[d.reason] ?? 'could not be used'}
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * The other half of the data-quality truth, and the easier one to leave out: documents that DID feed the
 * underwrite but were not read all the way through. Deliberately a separate banner from `UnreadSources` —
 * saying "could not be used" about a document that WAS used would be its own kind of dishonesty, and
 * saying nothing at all would let a half-read rent roll score as a complete one.
 */
/**
 * Documents the EXTRACTION read only part of — or none of — because the package overran its text caps.
 *
 * 🔴 Different from `PartiallyReadSources` in cause and owner. That one is the document store's OCR
 * stopping early; this is OUR prompt budget (20,000 characters a document, 120,000 in all). Both mean
 * figures in the unread part were not seen, and both used to be invisible to the reader — this one
 * entirely: the cut was marked to the model and a document past the total budget was skipped silently.
 * Named per document, with how much was read, because "a limit was hit" without WHERE is not actionable.
 */
/** A read limit, tagged with the pass whose caps produced it. */
export interface PassReadLimit extends ExtractionReadLimit {
  pass: 'extraction' | 'source agreement' | 'construction budget';
}

/**
 * Every pass's read limits on one run — extraction, reconciliation (source agreement), and the
 * construction budget. They share one budgeting loop (`document-budget.ts`) and one disclosure, because
 * three banners saying "not read in full" would read as three problems rather than one fact.
 */
export function readLimitsOf(
  // The REAL result type, not a structural lookalike: an inline shape reads fields no symbol-resolving
  // check can attribute, and the orphan-field sweep then reports them unread.
  result: Pick<IngestionResult, 'extraction' | 'reconciliation' | 'constructionBudget'>,
): PassReadLimit[] {
  return [
    ...(result.extraction?.readLimits ?? []).map((l) => ({ ...l, pass: 'extraction' as const })),
    ...(result.reconciliation?.readLimits ?? []).map((l) => ({
      ...l,
      pass: 'source agreement' as const,
    })),
    ...(result.constructionBudget?.readLimits ?? []).map((l) => ({
      ...l,
      pass: 'construction budget' as const,
    })),
  ];
}

export function ExtractionReadLimits({ limits }: { limits: PassReadLimit[] }) {
  if (limits.length === 0) return null;
  // DISTINCT documents in the heading: one rent roll cut by two checks is one document, not two.
  const docs = new Set(limits.map((l) => l.document)).size;
  return (
    <section className="notice warn unread">
      <strong>
        {docs} source document{docs === 1 ? ' was' : 's were'} not read in full
      </strong>{' '}
      — the package was larger than the text these checks read, so figures stated only in the unread
      part were not seen by the check named beside each.
      <ul className="unread__list">
        {limits.map((l, i) => (
          <li key={`${l.pass}-${l.document}-${i}`}>
            {l.document} —{' '}
            {l.kind === 'omitted'
              ? 'not read: the package ran out of room before this document'
              : `first ${l.charsRead.toLocaleString('en-US')} of ${l.charsTotal.toLocaleString('en-US')} characters read`}{' '}
            ({l.pass})
          </li>
        ))}
      </ul>
    </section>
  );
}

function PartiallyReadSources({ partial }: { partial: PartialDocument[] }) {
  return (
    <section className="notice warn unread">
      <strong>
        {partial.length} source document{partial.length === 1 ? '' : 's'} could only be read in part
      </strong>{' '}
      — these did inform the underwrite, but the document store could not extract all of their text,
      so figures stated in the unread portion were not seen.
      <ul className="unread__list">
        {partial.map((d, i) => (
          <li key={d.documentId ?? `${d.filename ?? 'doc'}-${i}`}>
            {d.filename ?? d.documentId ?? 'A document'} — partially read (scanned document, text
            extraction incomplete)
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * The program-type assumption — and it is the only assumption on this page that can move the verdict by
 * a quarter of the scale.
 *
 * `isLIHTC` switches off the Value Creation Gate AND the IRR return band, and re-bases the expected
 * expense ratio and vacancy. The engine reads it as `!!deal.isLIHTC`, so a package that never states the
 * program type is treated EXACTLY like one that states "market-rate" — and both take the gate's
 * structural penalty. On The Commons that is −15 points, and 37-vs-62, resting on an assumption nobody
 * declared and the report never mentioned.
 *
 * The engine's default is right (assume market-rate; it is the conservative read). What was wrong was
 * making it silently. So: whenever the program type is LOAD-BEARING — the gate fired, or the gate was
 * skipped because we believe it is LIHTC — say which way we went, on what evidence, and what it cost.
 */
/** How a pinned value reads to a person: the program type in words, a financing program as named. */
function pinValueLabel(field: RecordPin['field'], v: RecordPin['modelValue']): string {
  if (field === 'isLIHTC') return v === true ? 'LIHTC / affordable' : 'market-rate';
  return String(v);
}

/**
 * "pinned on the record by <name> (<date>); the documents read <modelValue>" — the disclosure every
 * record pin carries (ADR-0069 N27). The date is the ISO day, so it reads the same in every timezone.
 */
export function recordPinText(pin: RecordPin): string {
  const who = pin.recordedByName?.trim() || 'an unnamed user';
  const read =
    pin.modelValue === null
      ? 'the documents do not state it'
      : pin.modelValue === pin.recordValue
        ? 'the documents agree'
        : `the documents read ${pinValueLabel(pin.field, pin.modelValue)}`;
  // The reasoning is on the record fact, not on the run — say where it is rather than repeat it.
  return `pinned on the record by ${who} (${pin.recordedAt.slice(0, 10)}), reason on the record; ${read}`;
}

export function ProgramTypeAssumption({
  extraction,
  score,
}: {
  extraction: ExtractionSummary;
  score: InvestmentScore | null;
}) {
  /*
    ⚠️ `extraction.deal` IS THE MODEL'S READING. When the record pinned the program (ADR-0069 N27), the
    run scored the RECORD's value, so the pin decides what this panel says — reading the deal alone
    would describe a program the score did not use.
  */
  const pins = extraction.recordPins ?? [];
  const lihtcPin = pins.find((p) => p.field === 'isLIHTC');
  const financingPin = pins.find((p) => p.field === 'financingProgram');
  const isLIHTC = lihtcPin ? lihtcPin.recordValue === true : extraction.deal?.isLIHTC === true;
  const stated = (extraction.provenance ?? []).find((p) => p.field === 'isLIHTC');
  // The gate is the ONLY source of a structural penalty (engine `scoring.ts` reads it off `valueGate`),
  // so a structural penalty means it fired — i.e. we underwrote this as market-rate.
  const gateFired = (score?.structuralPenalty ?? 0) < 0;

  // Not load-bearing: the gate neither fired nor was exempted (e.g. no dev spread computable), and the
  // record pinned nothing. Say nothing. A pin is ALWAYS said: it overrode what the documents read.
  if (!gateFired && !isLIHTC && pins.length === 0) return null;

  return (
    <section className={`notice ${stated || lihtcPin ? '' : 'warn '}program`}>
      <strong>Program type — {isLIHTC ? 'LIHTC / affordable' : 'market-rate'}</strong>{' '}
      {lihtcPin ? (
        <>({recordPinText(lihtcPin)}).</>
      ) : stated ? (
        <>
          (stated in the package, {stated.confidence} confidence
          {stated.sourceDoc ? ` — ${stated.sourceDoc}` : ''}).
        </>
      ) : (
        <>
          — <em>assumed. The documents do not state it.</em>
        </>
      )}{' '}
      {isLIHTC ? (
        <>
          The Value Creation Gate does <strong>not</strong> apply to LIHTC deals, so the development
          spread was not scored. If this deal is in fact market-rate, the gate would apply and the
          score would fall.
        </>
      ) : (
        <>
          The Value Creation Gate was applied on that basis, costing{' '}
          <strong>{Math.abs(score?.structuralPenalty ?? 0)} points</strong>. If this deal is LIHTC,
          the gate does <strong>not</strong> apply and the score would rise materially.
        </>
      )}
      {financingPin && (
        <p className="program__prose">
          <strong>Financing program — {String(financingPin.recordValue)}</strong> (
          {recordPinText(financingPin)}). It selects the loan-to-cost covenant cap.
        </p>
      )}
      {(extraction.recordPinWarnings ?? []).map((w) => (
        <p className="program__prose" key={w}>
          ⚠ {w}.
        </p>
      ))}
      {!stated && !lihtcPin && (
        <p className="program__prose">
          This is the single assumption on this page most able to change the verdict — confirm it
          before relying on the score.
        </p>
      )}
    </section>
  );
}

/**
 * Source Agreement (Reconciliation, ADR-0029): where the deal package's source documents disagree on a
 * figure, and by how much. Presentational — the underwrite used the proforma-preferred value; this flags
 * the disagreements so they aren't silently trusted. Findings arrive engine-sorted, most-severe first.
 */
function SourceAgreement({ reconciliation }: { reconciliation: Reconciliation }) {
  const { findings, materialCount, reconcileCount, agreementCount, narrative } = reconciliation;
  return (
    <section className="notice warn reconcile">
      <strong>Source agreement</strong> — across the source documents: {materialCount} material,{' '}
      {reconcileCount} to reconcile, {agreementCount} in agreement.
      {narrative && <p className="reconcile__prose">{narrative}</p>}
      <ul className="flags">
        {findings.map((f, i) => (
          <li key={`${f.field}-${i}`} className={`flag flag--${f.severity}`}>
            <span className="flag__sev">{f.severity}</span> {f.message}
            {/*
              Which source the underwrite actually trusted (ADR-0029, #1163). `carriedSource` was
              computed, persisted and typed to this client for months with no reader, so a deal could
              underwrite on a figure no document stated and nothing on screen said so. The UNSUPPORTED
              case is carried by `f.message`, which the engine formats — the SPA does not re-derive a
              field's kind to format money vs a rate vs a count.
            */}
            {f.carriedSource && (
              <span className="flag__src">underwritten on {f.carriedSource}</span>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * Land basis (ADR-0034 D5a): the sponsor's claimed land cost held against the county public record.
 * Two honest outcomes. `checked` — the recorded deed and the assessor's land value CORROBORATE a
 * basis, so the sponsor's divergence from it is a real signal (a `flag` level warrants attention). The
 * severity styling mirrors Source Agreement so a `flag` reads as a warning, a `log` as informational.
 * `unverifiable` — the county's own two facts disagree, so a basis cannot be established: the panel
 * says exactly that and pointedly does NOT accuse the sponsor (the Oxford Commons case). Presentational
 * — the persisted variance is the durable record; the engine owns every number.
 */
/** Plain-language names for the outside sources, matching the report's Outside checks card. */
const FAILED_CHECK_LABELS: Record<string, string> = {
  landBasis: 'the county land record',
  siteContext: 'site context (flood, demographics, labour, permits, rents)',
  capRateSpread: 'benchmark interest rates',
  sponsorFilings: 'SEC filings',
  reconciliation: 'cross-document figure agreement',
  propertyTax: 'the county tax rate',
};

/**
 * Checks that were attempted and fell over.
 *
 * ⚠️ Why this is its own panel rather than silence: an underwrite deliberately survives a flaky third
 * party — the resolver throws, the error is logged, and the run completes. That is right, and it used to
 * be indistinguishable from a check that ran cleanly and found nothing. One is a reason to trust the
 * report slightly less; the other is not.
 */
export function ExternalCheckFailures({ failed }: { failed: readonly string[] }) {
  const named = failed.map((k) => FAILED_CHECK_LABELS[k] ?? k);
  return (
    <section className="card">
      <h3 className="card__title">Checks that could not complete</h3>
      <p className="card__prose">
        This underwriting could not reach {named.join(', ')}. The deal was analysed without{' '}
        {named.length === 1 ? 'it' : 'them'}, so nothing here is corroborated against{' '}
        {named.length === 1 ? 'that source' : 'those sources'}. Re-running the deal will try again.
      </p>
    </section>
  );
}

/**
 * The FOURTH state. `ExternalCheckFailures` above says a check TRIED and broke; this says a check never
 * ran because the deal held nothing to key it on — no address for the county record, no sponsor name
 * for the SEC. Kept separate on purpose: telling a reader a government source was unreachable, when the
 * gap is actually in the document package, points the doubt at the wrong party AND hides the one thing
 * they could act on.
 *
 * Found by the live run on 2026-07-31 that was meant to VERIFY the other two — the deal extracted no
 * address, so `landBasis` and `siteContext` were declared wired, threw nothing, and rendered nothing.
 */
export function ExternalChecksUnkeyed({ unkeyed }: { unkeyed: readonly string[] }) {
  const named = unkeyed.map((k) => FAILED_CHECK_LABELS[k] ?? k);
  const missing = unkeyed.every((k) => k === 'sponsorFilings')
    ? 'a sponsor name'
    : unkeyed.includes('sponsorFilings')
      ? 'a property address and a sponsor name'
      : 'a property address';
  return (
    <section className="card">
      <h3 className="card__title">Checks that could not be run</h3>
      <p className="card__prose">
        This deal was not checked against {named.join(', ')}, because the documents did not state{' '}
        {missing}. Nothing failed — there was simply no way to look these up. Add {missing} to the
        package and re-run to have {named.length === 1 ? 'it' : 'them'} checked.
      </p>
    </section>
  );
}

export function LandBasisPanel({ landBasis }: { landBasis: LandBasis }) {
  if (landBasis.kind === 'unverifiable') {
    return (
      <section className="notice reconcile">
        <strong>Land basis</strong> — the public record cannot establish a basis: the recorded deed
        ({currencyCompact(landBasis.recordedSaleAmount)}) and the assessor&rsquo;s land value (
        {currencyCompact(landBasis.assessedLandValue)}) disagree by{' '}
        {percentFromRatio(landBasis.disagreementPct, 0)}. The claimed land cost is not flagged — the
        county&rsquo;s own records conflict.
        <p className="reconcile__prose">
          Source: {landBasis.source}
          {retrievedSuffix(landBasis.asOf)}.
        </p>
        <StaleRecordNote asOf={landBasis.asOf} />
      </section>
    );
  }
  // A stale county record is REPORT-ONLY (Brad, 2026-09-24): no variance was raised, so no warning
  // styling either — the panel must not look like a finding the lifecycle is acting on.
  const stale = landBasis.asOf?.stale === true;
  const sev = landBasis.level === 'flag' && !stale ? 'warn' : '';
  return (
    <section className={`notice ${sev} reconcile`}>
      <strong>Land basis</strong> — the claimed land cost ({currencyCompact(landBasis.claimed)})
      diverges {percentFromRatio(landBasis.magnitudePct, 0)} from the corroborated public-record
      basis ({currencyCompact(landBasis.basis)}).
      <p className="reconcile__prose">
        Basis corroborated by the recorded deed and the assessor&rsquo;s land value. Source:{' '}
        {landBasis.source}
        {retrievedSuffix(landBasis.asOf)}.
      </p>
      <StaleRecordNote asOf={landBasis.asOf} />
      {stale && landBasis.level !== 'log' && (
        <p className="reconcile__prose">
          Not raised as a variance: a divergence found against an out-of-date record is reported
          only, and is checked again on the next run with a current one.
        </p>
      )}
    </section>
  );
}

/**
 * The wired checks the Outside checks card must NOT call `checked` (#1540): the one our model cannot do
 * (Indiana's cap), the ones that declined on a missing input, the ones that threw, and the ones the run
 * held nothing to key on. Each was declared wired, and each looked at nothing it could report on.
 */
export function notCheckedFrom(result: {
  propertyTax?: { kind: string } | null;
  externalChecksDeclined?: readonly { check: string; reason: string }[];
  externalCheckFailures?: readonly string[];
  externalChecksUnkeyed?: readonly string[];
}): string[] {
  const out = new Set<string>();
  if (result.propertyTax?.kind === 'notModeled') out.add('propertyTax');
  for (const d of result.externalChecksDeclined ?? []) out.add(d.check);
  for (const k of result.externalCheckFailures ?? []) out.add(k);
  for (const k of result.externalChecksUnkeyed ?? []) out.add(k);
  return [...out].sort();
}

/**
 * The proforma's stabilized property tax against the jurisdiction's own effective rate (Municipality,
 * module 7).
 *
 * 🔴 **THE ONE THING THIS PANEL MUST NEVER DO IS QUOTE THE PARCEL'S CURRENT TAX BILL AS THE
 * COMPARISON.** On a ground-up deal the parcel is a vacant lot — The Commons is 5.51 acres taxed at
 * $8,389 against a proforma tax on 240 finished units — so that reads as the sponsor understating by
 * thousands of percent. The bill is not on the wire for exactly this reason. What IS shown is the
 * RATE, which transfers, and the tax it implies on the FINISHED building. The copy has to make the
 * reader see that, which is why the rate and the assessed base are stated rather than just the delta.
 *
 * ⚠️ No severity styling and no ladder level: this is REPORT ONLY until it has run on real deals. It
 * is neutral (`notice reconcile`, never `warn`) whatever the delta, because a magnitude it has never
 * been calibrated against must not be dressed as a graded finding.
 */
export function PropertyTaxPanel({ propertyTax }: { propertyTax: PropertyTax }) {
  // The check did not run, and the panel says why rather than vanishing (#1532). The reason is about
  // our model, never the record or the sponsor, so it is neutral and quotes no rate. No stale-record
  // note: it says "this check used the last copy on record", and this check used nothing.
  if (propertyTax.kind === 'notModeled') {
    return (
      <section className="notice reconcile">
        <strong>Property tax</strong>: not checked. {propertyTax.reason}
        <p className="reconcile__prose">
          The proforma&rsquo;s tax stands as submitted. Source: {propertyTax.source}
          {retrievedSuffix(propertyTax.asOf)}.
        </p>
      </section>
    );
  }
  if (propertyTax.kind === 'unverifiable') {
    return (
      <section className="notice reconcile">
        <strong>Property tax</strong> — the county record cannot establish a credible rate for this
        jurisdiction ({percentFromRatio(propertyTax.effectiveRate, 2)} effective), so the
        proforma&rsquo;s tax is not challenged. The public record is the problem here, not the
        sponsor&rsquo;s figure.
        <p className="reconcile__prose">
          Source: {propertyTax.source}
          {retrievedSuffix(propertyTax.asOf)}.
        </p>
        <StaleRecordNote asOf={propertyTax.asOf} />
      </section>
    );
  }
  const understated = propertyTax.deltaAnnual > 0;
  return (
    <section className="notice reconcile">
      <strong>Property tax</strong> — the proforma states {currency(propertyTax.claimedAnnualTax)} a
      year. This jurisdiction levies {percentFromRatio(propertyTax.effectiveRate, 2)} on assessed
      value, which on the stabilized building ({currencyCompact(propertyTax.impliedAssessedValue)}{' '}
      assessed) implies {currency(propertyTax.impliedAnnualTax)} —{' '}
      {currency(Math.abs(propertyTax.deltaAnnual))} {understated ? 'more' : 'less'} than claimed (
      {percentFromRatio(propertyTax.magnitudePct, 0)})
      {propertyTax.deltaPerDoor === null
        ? ''
        : `, ${currency(Math.abs(propertyTax.deltaPerDoor))} per door`}
      .
      <p className="reconcile__prose">
        The rate is derived from this parcel&rsquo;s own recorded tax and assessed value, at an
        assessment ratio of {percentFromRatio(propertyTax.assessmentRatio, 0)} taken from the
        county&rsquo;s assessed-to-market relationship. The parcel&rsquo;s current bill is not the
        comparison — it is land today, not the finished building. Source: {propertyTax.source}
        {retrievedSuffix(propertyTax.asOf)}.
      </p>
      <StaleRecordNote asOf={propertyTax.asOf} />
    </section>
  );
}

/**
 * The exit cap held against the risk-free rate. Deliberately states the magnitude and the benchmark's
 * own date rather than delivering a verdict: a benchmark is not a quote and a spread is not a forecast,
 * so the reader is given the two rates and left to judge. `inverted` warrants attention — the exit cap
 * sits at or below the risk-free rate, implying no compensation for illiquidity or operating risk.
 */
export function CapRateSpreadPanel({ capRateSpread }: { capRateSpread: CapRateSpread }) {
  const { exitCapRate, riskFreeRate, spreadBps, band, observedOn, source } = capRateSpread;
  // Only `inverted` earns alarm treatment. A thin premium is worth a look, not a warning — cap
  // rates move independently of Treasuries and thin can be defensible on a specific asset.
  const inverted = band === 'inverted';
  const premium = inverted
    ? `no risk premium (${spreadBps} bps)`
    : `a ${spreadBps} bps premium (${band})`;
  return (
    <section className={`notice ${inverted ? 'warn' : ''} reconcile evid evid--watch`}>
      <div className="evid__head">
        <strong>Exit cap reasonableness</strong>
        <span className="evid__chip evid__chip--watch">
          {inverted ? 'Worth attention' : 'Worth a look'}
        </span>
      </div>
      <p className="reconcile__prose">
        The exit cap ({percentFromRatio(exitCapRate, 2)}) carries {premium} over the risk-free rate
        ({percentFromRatio(riskFreeRate, 2)}).{' '}
        {inverted
          ? 'An exit cap at or below the risk-free rate implies investors accepting less return than Treasuries for a leveraged, illiquid asset.'
          : 'Cap rates move independently of Treasuries, so a thin premium can be defensible on a specific asset.'}
      </p>
      <p className="reconcile__prose">
        Benchmark: {source}, observed {observedOn}.
      </p>
    </section>
  );
}

/** Why no exit-cap reference was shown, in words, for a block that arrives without its sentence. */
const EXIT_CAP_GAP_TEXT: Record<string, string> = {
  'no-location': 'the package states no property city and state',
  'market-not-in-table': 'the deal’s metro is not one GGPathOS carries from the CBRE survey',
  'class-unresolved': 'no asset class could be established, and a class is never assumed',
  'product-type-out-of-scope': 'the CBRE table describes market-rate multifamily only',
  lihtc: 'the CBRE table describes market-rate multifamily, not tax-credit or affordable product',
};

/**
 * ADR-0069 N20 — the CBRE exit-cap REFERENCE, beside the exit-cap reasonableness panel.
 *
 * Shown only when the package states no exit cap. Every figure carries the label "reference, not
 * stated", and the panel says in words that none of it was used in any score: Brad's 2026-10-01
 * ruling makes this a reference a reader may weigh, never an input the engine used.
 */
export function ExitCapReferencePanel({ reference }: { reference: ExitCapReferenceView }) {
  const label = reference.label ?? 'reference, not stated';
  if (reference.kind !== 'reference') {
    return (
      <section className="notice reconcile evid evid--watch">
        <div className="evid__head">
          <strong>GGP exit cap reference</strong>
          <span className="evid__chip evid__chip--watch">Not shown</span>
        </div>
        <p className="reconcile__prose">
          {reference.sentence ??
            `No exit cap reference: the package states no exit cap, and ${
              EXIT_CAP_GAP_TEXT[reference.reason ?? ''] ?? 'none could be established'
            }.`}
        </p>
      </section>
    );
  }
  const range = reference.capRangeBps;
  return (
    <section className="notice reconcile evid evid--watch">
      <div className="evid__head">
        <strong>GGP exit cap reference</strong>
        <span className="evid__chip evid__chip--watch">{label}</span>
      </div>
      {range ? (
        <p className="reconcile__prose">
          {reference.market}, Class {reference.canonicalClass}:{' '}
          <strong>
            {percentFromRatio(range.low / 10_000, 2)} to {percentFromRatio(range.high / 10_000, 2)}
          </strong>{' '}
          ({label}).
        </p>
      ) : null}
      {/* The derivation, in parts, so a reader can check the arithmetic without the sentence. */}
      {reference.classARangeBps ? (
        <p className="reconcile__prose">
          Average of CBRE&apos;s Infill and Suburban Class A ranges:{' '}
          {percentFromRatio(reference.classARangeBps.low / 10_000, 2)} to{' '}
          {percentFromRatio(reference.classARangeBps.high / 10_000, 2)}
          {reference.classOffsetBps
            ? `, plus GGP's ${reference.classOffsetBps} bps Class ${reference.canonicalClass?.charAt(0) ?? ''} offset`
            : ''}
          {reference.vintage ? `; inputs: CBRE survey edition ${reference.vintage}` : ''}
          {reference.note ? `; ${reference.note}` : ''}.
        </p>
      ) : reference.vintage ? (
        <p className="reconcile__prose">Inputs: CBRE survey edition {reference.vintage}.</p>
      ) : null}
      <p className="reconcile__prose">
        {reference.sentence ??
          `The package states no exit cap. A GGP range for ${reference.market ?? 'this metro'}, Class ${
            reference.canonicalClass ?? ''
          }, built from CBRE survey inputs (${label}), is on the full report. It is not used in any score.`}
      </p>
    </section>
  );
}

/**
 * WHICH PARCEL every external check in this section actually ran against.
 *
 * ⚠️ The gap this closes: the APN was served on the seam and rendered nowhere, so **a resolve that
 * matched the WRONG parcel looked identical to one that matched the right one.** The panels below
 * report flood zone, demographics, and land basis with real authority, and a reader had no way to
 * see which piece of land any of it described. Found by `orphan:fields` on 2026-08-16 once the sweep
 * could see past a destructure; ruled by Brad the same day.
 *
 * It belongs HERE and not in `DealIdentity`, whose stated contract is asserted-never-resolved
 * ("no parcel, no CIK, no confirmation that the address resolves"). An APN is the opposite of an
 * assertion — it is the public record answering back, which is what the Sources section is for.
 *
 * `fips` is deliberately not rendered beside it: the county is named in words wherever it matters and
 * the five-digit code is a join key, not a fact a person acts on (see the allowlist entry).
 */
export function ParcelIdentityPanel({ site }: { site: SiteIdentity }) {
  const apn = site.apn?.trim();
  // No resolve, no claim. An empty "Parcel" line would imply we looked and found nothing, when the
  // ordinary case is that no parcel resolve ran at all — the external-check panels say which.
  if (!apn) return null;

  return (
    <section className="card">
      <h3 className="card__title">Parcel identity</h3>
      <p className="card__note">
        The address on this deal resolved to parcel <strong>{apn}</strong>. Every external check
        below ran against that parcel rather than against the address as it was typed — so if this
        is not the land you mean, nothing beneath it describes your deal.
      </p>
    </section>
  );
}

/**
 * Independently-sourced facts about the site and its county — flood zone, jobs, demographics, the
 * published rent floor, and the competing supply pipeline. None of this was fed to the model or used
 * in the score; it sits BESIDE the sponsor's claims so a reader can weigh them against a source the
 * sponsor does not control. A source that had nothing is simply omitted rather than shown as zero.
 */
export function SiteContextPanel({ siteContext }: { siteContext: SiteContext }) {
  const { flood, labor, demographics, fairMarketRent, buildingPermits } = siteContext;

  /** A figure the reader should be able to SEE, not read out of the middle of a sentence. */
  const figures: { key: string; value: string; label: string; down?: boolean }[] = [];
  const trend = labor?.employmentTrend12mo;
  if (trend !== null && trend !== undefined) {
    figures.push({
      key: 'trend',
      value: `${trend < 0 ? '−' : '+'}${percentFromRatio(Math.abs(trend), 1)}`,
      label: 'County jobs, 12 mo',
      down: trend < 0,
    });
  }
  if (buildingPermits?.unitsFivePlus != null) {
    figures.push({
      key: 'permits',
      value: buildingPermits.unitsFivePlus.toLocaleString(),
      label: 'Units permitted, 5+',
    });
  }
  const twoBed = fairMarketRent?.rentByBedrooms['2'];
  if (twoBed !== undefined) {
    // `currency`, not `currencyCompact`. A $1,711 rent rendered as "$2K" rounds away the digits
    // that make it a usable comparison — compact form is for headline millions, not monthly rent.
    figures.push({ key: 'fmr', value: currency(twoBed), label: 'Fair-market rent, 2BR' });
  }
  if (labor?.unemploymentRatePct != null) {
    figures.push({
      key: 'unemp',
      value: `${labor.unemploymentRatePct.toFixed(1)}%`,
      label: 'Unemployment',
    });
  }
  if (demographics?.medianHouseholdIncome != null) {
    figures.push({
      key: 'income',
      value: currency(demographics.medianHouseholdIncome),
      label: 'Median household income',
    });
  }

  // Flood stays prose: it is a categorical fact carrying a consequence, not a number to compare.
  const floodNote = flood
    ? flood.inSpecialFloodHazardArea
      ? `Flood zone ${flood.zone} — a special flood hazard area, so flood insurance is mandatory on a federally-backed loan.`
      : `Flood zone ${flood.zone} — outside the special flood hazard area.`
    : null;

  const ozNote = opportunityZoneNote(siteContext.opportunityZone ?? null);

  if (figures.length === 0 && floodNote === null && ozNote === null) return null;

  return (
    <section className="notice reconcile evid">
      <div className="evid__head">
        <strong>Independent site &amp; market record</strong>
        <span className="evid__chip">Context</span>
      </div>
      {figures.length > 0 && (
        <div className="evid__figs">
          {figures.map((f) => (
            <div key={f.key} className={`evid__fig${f.down ? ' evid__fig--down' : ''}`}>
              <b>{f.value}</b>
              <span>{f.label}</span>
            </div>
          ))}
        </div>
      )}
      {floodNote && <p className="reconcile__prose">{floodNote}</p>}
      {ozNote && <p className="reconcile__prose">{ozNote}</p>}
      <p className="reconcile__prose">
        Public sources, not the sponsor&rsquo;s documents: {sourceList(siteContext)}. None of this
        was used in the score.
      </p>
      {staleSources(siteContext).length > 0 && (
        <p className="reconcile__prose">
          <strong>Not current:</strong> {staleSources(siteContext).join(', ')} could not be reached
          for this run, so the last copy on record is shown instead.
        </p>
      )}
    </section>
  );
}

/**
 * Public securities filings whose registrant name MATCHES the sponsor name someone typed at submission.
 *
 * ⚠️ **THE WHOLE DESIGN IS THE DISCLAIMER, and it is not decoration.** Two companies can share a name,
 * and a sponsor may file under an affiliate that shares none of it. If this panel reads as "here is the
 * sponsor's track record", it has told the reader something nobody verified — a false endorsement, or,
 * if the filings look bad, a false accusation. So:
 *
 *   • the registrant names render VERBATIM, exactly as EDGAR published them, including the `(CIK …)`
 *     suffix — that string is what lets a reader see whether this is really their sponsor;
 *   • the SEARCHED NAME is shown, so the match is auditable rather than asserted;
 *   • the chip says `UNVERIFIED`, not `MATCH`;
 *   • no sentence in here calls the registrant "the sponsor".
 *
 * The count figures are deliberately about the SEARCH ("filings on record", "most recent"), never about
 * the sponsor's experience — "3 prior offerings" would be exactly the claim this must not make.
 */
export function SponsorFilingsPanel({ sponsorFilings }: { sponsorFilings: SponsorFilings }) {
  const { query, totalHits, filings } = sponsorFilings;
  if (filings.length === 0) return null;

  // Newest filing year — EDGAR ranks newest-first, but sort rather than trust the order, because a
  // "most recent" figure that is quietly the oldest is worse than no figure.
  const years = filings.map((f) => f.filedOn.slice(0, 4)).filter((y) => /^\d{4}$/.test(y));
  const mostRecent = years.length > 0 ? years.sort().at(-1) : null;

  return (
    <section className="notice reconcile evid">
      <div className="evid__head">
        <strong>Public filings matching this name</strong>
        <span className="evid__chip">Unverified</span>
      </div>
      {/*
        ⚠️ THE CAVEAT LEADS. It used to close the panel, under ten rows of filings, in italic — and two
        hero figures (`27` · `2025`) opened it at display size.
        Both statements were true and the ORDER made them lie. Rendered large and first, a count and a
        recent year are the two most credential-like facts available: a reader who scans nothing else
        takes away "27 filings, active through 2025" — an established, busy sponsor — and the correction
        arrives after they have already formed the impression. Hierarchy beats copy.
        Seen live on the first real run: the 27 matches for "GGP" are Greenspring Global Partners (a VC
        firm), Brookfield Property REIT (the former General Growth), assorted Delaware SPVs, and
        `Harlan SPVI LLC` — which does not contain the string at all, because EDGAR full-text search
        matches a filing that merely MENTIONS it. Not one is Good Good Plans.
      */}
      <p className="filings__lede">
        EDGAR matched these on the name <strong>“{query}”</strong> alone — a name match is not an
        identity. Different companies share names, and a sponsor may file under an affiliate. Read
        the registrant names below and judge whether any of them is this sponsor.
      </p>
      <ul className="filings">
        {filings.map((f) => (
          <li key={f.accession} className="filings__row">
            {/* Registrant name FIRST and verbatim — it is the thing a reader has to judge. */}
            <b className="filings__who">
              {f.registrantNames.join(' · ') || '(registrant unnamed)'}
            </b>
            <span className="filings__meta">
              Form {f.formType} · filed {f.filedOn}
              {f.incorporationStates.length > 0 &&
                ` · incorporated ${f.incorporationStates.join('/')}`}
              {f.businessStates.length > 0 && ` · business ${f.businessStates.join('/')}`}
            </span>
          </li>
        ))}
      </ul>
      {totalHits > filings.length && (
        <p className="reconcile__prose">
          Showing {filings.length} of {totalHits.toLocaleString()} — EDGAR returns the newest first.
        </p>
      )}
      {/* The match caveat now LEADS the panel; what stays here is the count and the score statement —
          facts about the SEARCH, stated plainly rather than as display figures. */}
      <p className="reconcile__prose">
        {totalHits.toLocaleString()} filing{totalHits === 1 ? '' : 's'} mention this name
        {mostRecent ? `, the most recent filed in ${mostRecent}` : ''}. That is a property of the
        search, not of this sponsor. Nothing here was used in the score.
      </p>
      {/*
        🔴 ADR-0015 D3. A stale filing list is invisible on its own: a filing made since the cached
        copy just is not there, and the count above reads as the whole answer. So it is said.
      */}
      {sponsorFilings.asOf?.stale ? (
        <p className="reconcile__prose">
          <strong>Not current:</strong> SEC EDGAR could not be reached for this run, so this is the
          list on record from {timestamp(sponsorFilings.asOf.fetchedAt)}. A filing made since then
          would not appear here.
        </p>
      ) : sponsorFilings.asOf ? (
        <p className="reconcile__prose">
          Retrieved from SEC EDGAR {timestamp(sponsorFilings.asOf.fetchedAt)}.
        </p>
      ) : null}
    </section>
  );
}

/**
 * The Opportunity Zone sentence — BOTH maps, each dated (ADR-0069 N27, Brad 2026-09-28).
 *
 * ⚠️ THE TENSE IS DECIDED WHEN IT IS READ, NOT WHEN THE RUN WAS SAVED. The 2018 map lapses at the end
 * of 2026 and the 2027 map will be certified; a run saved in September and read in February must not say
 * the 2018 map "expires" or that the 2027 map "is not yet certified". So the 2018 clause is tensed from
 * today's date, and the 2027 clause is stated AS OF the run's date. Never "eligible" as if designated.
 */
export function opportunityZoneNote(
  oz: NonNullable<SiteContext['opportunityZone']> | null,
  today: string = new Date().toISOString().slice(0, 10),
): string | null {
  if (!oz || oz.map2018.designated === null) return null;
  const tract = oz.tract2010 ? ` (2010 census tract ${oz.tract2010})` : '';
  const kind = oz.map2018.tractType === 'contiguous' ? ', as a contiguous tract' : '';
  const map2018 = oz.map2018.designated
    ? `In a designated Opportunity Zone on the 2018 map${tract}${kind}`
    : `Not in an Opportunity Zone on the 2018 map${tract}`;
  const expiry =
    today <= oz.map2018.expires
      ? `, which expires ${formatIsoDate(oz.map2018.expires)}.`
      : `, which expired ${formatIsoDate(oz.map2018.expires)}.`;
  const asOf = oz.asOf ? formatIsoDate(oz.asOf.fetchedAt.slice(0, 10)) : null;
  const map2027 = asOf
    ? ` As of ${asOf}, the 2027–2036 map (in effect ${formatIsoDate(oz.map2027.effective)}) was not yet certified by Treasury, so whether the site is in a zone after 2026 was not known.`
    : ` The 2027–2036 map (in effect ${formatIsoDate(oz.map2027.effective)}) was not certified when this ran, so whether the site is in a zone after 2026 was not known.`;
  // Canon's own caveat (deal_analyzer_prompts §1): the designation is not the benefit.
  const caveat = oz.map2018.designated
    ? ' A designation alone confers nothing: the tax benefit requires investment through a Qualified Opportunity Fund.'
    : '';
  return `${map2018}${expiry}${map2027}${caveat}`;
}

/** "Dec 31, 2026" from "2026-12-31", with no timezone drift. */
function formatIsoDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  return y && m && d ? `${months[m - 1]} ${d}, ${y}` : iso;
}

/** The site-context members that answered, in render order. */
function siteSources(siteContext: SiteContext): { source: string; asOf?: RecordAsOf }[] {
  return [
    siteContext.flood,
    siteContext.labor,
    siteContext.demographics,
    siteContext.fairMarketRent,
    siteContext.buildingPermits,
    // Named as a source only when its sentence renders — an unknown tract says nothing.
    siteContext.opportunityZone?.map2018.designated != null ? siteContext.opportunityZone : null,
  ].filter((m): m is NonNullable<typeof m> => m !== null && m !== undefined);
}

/** Each source once, with when it was retrieved when the run recorded that. */
function sourceList(siteContext: SiteContext): string {
  const seen = new Map<string, string>();
  for (const { source, asOf } of siteSources(siteContext)) {
    if (!seen.has(source)) seen.set(source, `${source}${retrievedSuffix(asOf)}`);
  }
  return [...seen.values()].join('; ');
}

/**
 * The sources whose figures are a STALE fallback — named, with the age of the copy (ADR-0015 D3).
 * Empty for every run recorded before the flag existed: absent is "not stated", never "stale".
 */
function staleSources(siteContext: SiteContext): string[] {
  const names = new Map<string, string>();
  for (const { source, asOf } of siteSources(siteContext)) {
    if (asOf?.stale && !names.has(source)) {
      names.set(source, `${source} (copy from ${timestamp(asOf.fetchedAt)})`);
    }
  }
  return [...names.values()];
}

/**
 * ", retrieved Sep 20, 2026, 12:00 PM" for a current record; nothing when the run did not record it
 * or when the record is stale (the stale note says it louder, and saying it twice reads as two facts).
 */
function retrievedSuffix(asOf: RecordAsOf | undefined): string {
  if (!asOf || asOf.stale) return '';
  return `, retrieved ${timestamp(asOf.fetchedAt)}`;
}

/**
 * 🔴 ADR-0015 D3. The source was DOWN for this run and the last cached copy was used. Before this, a
 * stale county record reached these panels looking exactly like a fresh one — on a finding that can
 * flag a sponsor. The figures are still shown (stale-but-sourced beats nothing); their age is not
 * allowed to be silent.
 */
function StaleRecordNote({ asOf }: { asOf: RecordAsOf | undefined }) {
  if (!asOf?.stale) return null;
  return (
    <p className="reconcile__prose">
      <strong>Not current:</strong> the county source could not be reached for this run, so this
      check used the last copy on record, retrieved {timestamp(asOf.fetchedAt)}.
    </p>
  );
}

/**
 * Where a workbook's bytes came from. An ALLOWLIST — an unrecognised origin renders nothing rather
 * than a guess, the same rule the rent-check reasons follow. Never default a disclosure.
 */
const SPINE_ORIGIN_COPY: Record<string, string> = {
  upload: 'uploaded with this deal',
  connector: "pulled from the project's connected folder",
};

/**
 * A spine value for display. Numbers get thousands separators; a blank is an em dash, not a zero.
 *
 * ⚠️ SUB-1 VALUES KEEP THEIR PRECISION. Spine rates are ratios by convention (`loanRate` 0.0625,
 * `exitCapRate` 0.055), and two decimal places would render a 6.25% loan as "0.06" and a 5.5% exit
 * cap as "0.06" — the same string for two different numbers, on the panel whose entire purpose is
 * letting a reader check a figure against a cell.
 */
function hasValue(v: number | string | null | undefined): boolean {
  return (
    (typeof v === 'number' && Number.isFinite(v)) || (typeof v === 'string' && v.trim() !== '')
  );
}

function spineValueText(v: number | string | null | undefined): string {
  if (typeof v === 'number' && Number.isFinite(v)) {
    const digits = v !== 0 && Math.abs(v) < 1 ? 6 : 2;
    return v.toLocaleString('en-US', { maximumFractionDigits: digits });
  }
  if (typeof v === 'string' && v.trim() !== '') return v.trim();
  return '—';
}

/**
 * Who is OFFERED the governing-proforma actions (ADR-0069 N32): the OWNER only — the server's
 * `basis:lock` (Brad, 2026-10-08). ⚠️ Deliberately narrower than the other deal-fact pins, which an
 * admin may set. Drawing only; the server refuses anyone else regardless.
 */
export function mayPinGoverningProforma(role: unknown): boolean {
  return role === 'owner';
}

/** Why a governing-proforma pin could not govern this run (ADR-0069 N32), in the reader's words. */
export const GOVERNING_REASON_COPY: Record<
  NonNullable<NonNullable<ProformaSpineRead['governing']>['reason']>,
  string
> = {
  'not-on-record': 'that document is no longer on the project record',
  'not-read': 'that workbook was not among those read on this run',
  'not-a-template': 'that workbook does not read as a GGP template',
  'not-a-workbook': 'that document is not a spreadsheet, so it cannot be a proforma',
  withdrawn:
    'that document was withdrawn from the project record after it was pinned; pin another file or release the pin',
  // #1408 — not "Nexus did not ingest": the run also says this when Nexus DID ingest the file but it
  // could not be matched to a document on the record with certainty (a stale deduplicated copy, one
  // id on two different files, an older run's row). The copy states only what is true in every case.
  'upload-unidentified':
    'this run read an uploaded workbook that could not be matched to a document on the record with certainty, so it cannot confirm it is the pinned file',
};

/**
 * "Use this as the governing proforma" (ADR-0069 N32). Writes a `deal_fact` pin by document id, with
 * the written reasoning canon R9 requires. It takes effect on the project's NEXT run; this report is
 * not rewritten, and the action says so rather than implying the figures above just changed.
 */
export function GoverningPinAction({
  projectCode,
  documentId,
  filename,
}: {
  projectCode: string;
  /** The document to pin, or `null` to RELEASE the record's pin (the documents decide again). */
  documentId: string | null;
  filename: string;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [status, setStatus] = useState<'editing' | 'saving' | 'done'>('editing');
  const [error, setError] = useState<string | null>(null);
  const releasing = documentId === null;
  if (status === 'done') {
    return (
      <span className="prov__note">
        {releasing
          ? 'Pin released — the documents decide again from the next run.'
          : 'Pinned as the governing proforma — applies from the next run.'}
      </span>
    );
  }
  if (!open) {
    return (
      <button type="button" className="link-btn" onClick={() => setOpen(true)}>
        {releasing ? 'Release the governing-proforma pin' : 'Use this as the governing proforma'}
      </button>
    );
  }
  return (
    <form
      className="prov__note"
      onSubmit={(e) => {
        e.preventDefault();
        if (reason.trim() === '') return;
        setStatus('saving');
        setError(null);
        void pinGoverningProforma(projectCode, documentId, reason.trim()).then((r) => {
          if (r.ok) {
            setStatus('done');
          } else {
            setStatus('editing');
            setError(r.error);
          }
        });
      }}
    >
      <label>
        {releasing ? 'Why release the pin?' : `Why does ${filename} govern?`}{' '}
        <textarea
          value={reason}
          maxLength={2000}
          required
          disabled={status === 'saving'}
          onChange={(e) => setReason(e.target.value)}
        />
      </label>
      <button type="submit" disabled={status === 'saving' || reason.trim() === ''}>
        {releasing ? 'Release' : 'Pin'}
      </button>
      {error && <span role="alert"> {error}</span>}
    </form>
  );
}

/**
 * The deterministic workbook read, and every figure it moved (ADR-0042 D4).
 *
 * ⚠️ THIS IS THE READER FOR A DISCLOSURE THAT HAD NONE. `INGEST_SPINE_OVERRIDE` has been live on
 * staging since 2026-08-10, and by 2026-08-12 had fired on four real runs, substituting up to fifteen
 * fields apiece — `noi`, `totalCost`, `hardCosts`, `equity`, `holdYears`, `loanRate`,
 * `annualDebtService`, `propertyClass`. Those are the inputs to every scored metric on the page. Each
 * substitution was recorded on `extraction.spineOverride` and read by NOTHING, which made the
 * override's own third rule false in practice: *"Nothing is silent. Every substitution is recorded
 * with the model's value, the workbook's value, and the cell. A moved number a reader cannot
 * reconcile is a moved number."* Recorded is not disclosed.
 *
 * ⚠️ And it is sharper than an absent panel. The field-provenance list rendered directly below is the
 * MODEL's — `extractionSummary` keeps the model's reading verbatim so the record holds both sides of
 * every disagreement. So on those runs the page showed one figure's provenance beside a DIFFERENT
 * figure's value, with nothing on the page to reconcile them.
 *
 * Both sides are shown for the same reason the record keeps both: "the workbook says 29,696,000"
 * is provenance, but "the workbook says 29,696,000 where we had read 26,026,000" is what lets a
 * reader who remembers the earlier number understand why it changed.
 */
export function DeterministicRead({
  spine,
  overrides,
  pinProjectCode,
}: {
  spine?: ProformaSpineRead;
  overrides?: SpineOverrideEntry[];
  /** Set only for the project OWNER (`basis:lock`), who alone may pin the governing proforma. */
  pinProjectCode?: string;
}) {
  // No workbook, no claim. A package of PDFs is not a failed read and must not render as one.
  if (!spine?.filename) return null;
  const moved = overrides ?? [];
  const origin = spine.bytesFrom ? SPINE_ORIGIN_COPY[spine.bytesFrom] : undefined;
  return (
    <div className="extract__spine">
      <p className="extract__spineline">
        <strong>Read from the workbook:</strong> {spine.filename}
        {origin ? ` — ${origin}` : ''}
        {typeof spine.fieldsLocated === 'number' ? ` · ${spine.fieldsLocated} fields located` : ''}
        {spine.template
          ? ` · GGP template ${spine.template.family}${
              spine.template.stated && spine.template.stated !== spine.template.family
                ? ` (its README says ${spine.template.stated})`
                : ''
            }`
          : ''}
      </p>
      {/*
        ADR-0069 N31 — a KNOWN template defect, shown where the figure is read. The value is kept (the
        model reads the same cell), so the only honest thing to do with the defect is to say it.
      */}
      {(spine.defects ?? []).map((d) => (
        <p className="extract__gaps" key={`${d.field}-${d.cell ?? ''}`}>
          <strong>Known template defect</strong>
          {d.cell ? ` (${d.cell})` : ''}: {d.reason}.
        </p>
      ))}
      {/*
        ⚠️ THE READER FOR #653. The spine now picks the most recently ingested proforma, but "we chose
        for you" is not a thing to do silently: two GGP-template workbooks in one project can be a
        superseded revision, a variant scenario, or a filing mistake, and only a person can tell which.
        Before this, the panel named one file and a reader had no way to learn a second existed — which
        is how The Commons underwrote from a 2026-07-20 workbook for twelve runs with its 2026-08-07
        replacement sitting unread in the same document set.

        Deliberately NOT phrased as a defect: on a project that genuinely holds a superseded revision
        this is correct behaviour being explained, not an error being confessed. It states what was
        read, what was passed over, and why — and leaves the judgement where it belongs.
      */}
      {/* ADR-0069 N32 — a pin that could not govern re-opens the question; say so, and why. */}
      {spine.governing && !spine.governing.applied && (
        <p className="extract__gaps">
          <strong>The governing proforma pinned on the record could not be used</strong> (
          {GOVERNING_REASON_COPY[spine.governing.reason ?? 'not-read']}), so no workbook figure was
          applied to the score.
        </p>
      )}
      {/*
        ADR-0069 N32 — "use the file read on this run" is offered OUTSIDE the collapsed panel, and
        whenever the question is open: rivals disagree, or a pin on the record could not govern. A
        stale pin on a project now holding one template would otherwise leave nothing to click.
      */}
      {pinProjectCode &&
        spine.documentId &&
        !spine.governing?.applied &&
        ((spine.rivalsDisagree?.length ?? 0) > 0 || spine.governing) && (
          <p className="extract__gaps">
            <GoverningPinAction
              projectCode={pinProjectCode}
              documentId={spine.documentId}
              filename={spine.filename}
            />
          </p>
        )}
      {/*
        ADR-0069 N32 — an OLD pin over a NEWER revision. Non-blocking (the pin is a person's word), but
        shown OUTSIDE the collapsed panel: the question may have moved since they answered it.
      */}
      {spine.governing &&
        (spine.rivalsDisagree ?? [])
          .filter((r) => r.newerThanPin)
          .map((r) => (
            <p className="extract__gaps" key={`newer-${r.documentId ?? r.filename}`}>
              <strong>A rival proforma was filed after the governing pin was set</strong>:{' '}
              {r.filename}
              {r.ingestedAt ? `, filed ${r.ingestedAt.slice(0, 10)}` : ''}; the pin dates from{' '}
              {spine.governing?.recordedAt.slice(0, 10)}.
            </p>
          ))}
      {/* A pin can always be released from here — the record is append-only, so this is how one stops. */}
      {pinProjectCode && spine.governing && (
        <p className="extract__gaps">
          <GoverningPinAction
            projectCode={pinProjectCode}
            documentId={null}
            filename={spine.governing.filename ?? spine.filename}
          />
        </p>
      )}
      {(spine.alsoPresent?.length ?? 0) > 0 && (
        <>
          {/* N32: shown OUTSIDE the collapsed panel. A reader must not have to expand it to learn
              whether a workbook figure reached the score. */}
          {(spine.rivalsDisagree?.length ?? 0) > 0 && (
            <p className="extract__gaps">
              The proforma workbooks in this project disagree on {disagreedFieldLabels(spine)}
              {spine.governing?.applied
                ? '. The project record names the governing proforma, and its figures were applied to the score.'
                : ', so no workbook figure was applied to the score. Which file governs is a question for the project team.'}
            </p>
          )}
          <details className="extract__details">
            <summary>
              {spine.alsoPresent?.length} other proforma workbook
              {spine.alsoPresent?.length === 1 ? '' : 's'} in this project{' '}
              {spine.alsoPresent?.length === 1 ? 'was' : 'were'} not used
            </summary>
            {spine.governing?.applied ? (
              <p className="extract__gaps">
                This project holds more than one GGP-template workbook. The project record names the
                one above as the governing proforma (pinned
                {spine.governing.recordedByName
                  ? ` by ${spine.governing.recordedByName}`
                  : ''} on {spine.governing.recordedAt.slice(0, 10)}); the others were parsed and
                are listed here, not used.
              </p>
            ) : (
              <p className="extract__gaps">
                This project holds more than one GGP-template workbook. The figures above were read
                from the most recently added one not named as superseded; the others were parsed but
                not used. If the wrong file governed, the fix is to the project&rsquo;s documents,
                not to this run.
              </p>
            )}
            <ul className="prov">
              {spine.alsoPresent?.map((o) => (
                <li className="prov__row" key={o.documentId ?? o.filename}>
                  <span className="prov__field">{o.filename}</span>
                  <span className="prov__src">
                    {o.ingestedAt ? `added ${o.ingestedAt.slice(0, 10)}` : 'date not recorded'}
                  </span>
                  {pinProjectCode && o.documentId && (
                    <GoverningPinAction
                      projectCode={pinProjectCode}
                      documentId={o.documentId}
                      filename={o.filename}
                    />
                  )}
                </li>
              ))}
            </ul>
          </details>
        </>
      )}
      {moved.length > 0 && (
        <details className="extract__details">
          <summary>
            {moved.length} figure{moved.length === 1 ? '' : 's'} came from the workbook, not from
            our reading of it
          </summary>
          {/*
            ⚠️ THIS COPY ONCE SAID "these are the values the score used", AND THAT WAS FALSE OF SOME OF
            THEM. Five spine fields — `unitSF`, `hardCosts`, `softCosts`, `equity`, `netRentableSF` —
            are real figures the workbook states that `DealInputs` has no slot for, so no metric reads
            them (`loanRate` is decided per run, #1287). On a real 15-field override six were spine-only
            then: 40% of the list moved
            nothing. Disclosing them is still right; calling them the score's inputs was not. The claim
            is now what the record supports — read from the workbook — with the ones that actually fed
            the underwrite marked individually.
          */}
          <p className="extract__gaps">
            These were read from the workbook rather than from our reading of the documents, each
            with the cell it came from. The ones marked <em>used in the underwrite</em> are what the
            score was computed from.
          </p>
          <ul className="prov">
            {moved.map((o, i) => {
              // A DERIVED value has no cell to cite — `netRentableSF` is `units × unitSF`, computed
              // from two other reads. Presenting that string as a cell reference would invite a
              // reader to go look for it in the workbook and find nothing.
              const derived = spine.fields?.[o.field]?.source === 'derived';
              /*
                ⚠️ THE CAVEAT THE WORKBOOK READ ATTACHED, AND IT CHANGES HOW THE FIGURE READS.
                The engine writes `note` ONLY when the parse had something to say, and the one that
                matters is `netRentableSF`: it is net-rentable area, NOT canon-basis GSF, and it
                understates the benchmark basis by 10-20%. A reader comparing a $/SF figure against a
                band without that sentence is comparing against the wrong denominator.

                It was served to this page and rendered by NOTHING — found by the orphan-field sweep,
                which could not see it while the guard matched names (ExtractionProvenance declares a
                `note` too, and that one IS undescribed model prose we deliberately do not show).
              */
              const note = spine.fields?.[o.field]?.note;
              return (
                <li className="prov__row" key={`${o.field}-${i}`}>
                  <span className="prov__field">{gapLabel(o.field)}</span>
                  <span className="prov__val">{spineValueText(o.spineValue)}</span>
                  <span className="prov__src">
                    {derived ? 'derived, not a cell' : (o.cell ?? 'cell not recorded')}
                  </span>
                  {/* ⚠️ Absent ⇒ SILENT. A run written before the flag existed cannot say either way,
                      and defaulting it to "used" would assert the very thing that was overstated. */}
                  {o.underwritingInput === true && (
                    <span className="prov__used">used in the underwrite</span>
                  )}
                  {/*
                    ⚠️ "we had read —" IS NOT A SENTENCE. `applySpineOverride` cannot emit an entry
                    with `filledGap: false` and no `modelValue`, but a row rehydrated from jsonb that
                    predates the flag can, and rendering an em dash there states nothing while looking
                    like it states something. Say what we know or say nothing.
                  */}
                  {o.filledGap ? (
                    <span className="prov__was">we had read no value</span>
                  ) : hasValue(o.modelValue) ? (
                    <span className="prov__was">we had read {spineValueText(o.modelValue)}</span>
                  ) : null}
                  {note && <span className="prov__note">{note}</span>}
                </li>
              );
            })}
          </ul>
        </details>
      )}
    </div>
  );
}

/** The fields rival proforma templates disagree on, in the reader's words, each named once. */
function disagreedFieldLabels(spine: ProformaSpineRead): string {
  return [...new Set(spine.rivalsDisagree?.flatMap((r) => r.fields))].map(gapLabel).join(', ');
}

/**
 * Which proforma governs, asked on a run that STOPPED for more documents (ADR-0069 N32, #1395).
 *
 * ⚠️ THE CASE THE PIN EXISTS FOR WAS THE ONE PAGE WITHOUT IT. Rival GGP templates that disagree
 * withhold the spine override; with no workbook figure applied, NOI is often missing; the run ends as
 * `needs_more_docs` — and that outcome rendered no spine panel, so the owner had nothing to click and
 * the "add a proforma" advice above was wrong (the proformas were there; they disagreed).
 *
 * Deliberately NOT `DeterministicRead`: that panel speaks of figures "applied to the score" and lists
 * moved figures "used in the underwrite", none of which is true of a run that produced no score. This
 * reuses its pieces — the disagreement sentence, `GOVERNING_REASON_COPY`, `GoverningPinAction` — and
 * offers the pin on EVERY candidate workbook, because here there is no "file read on this run" whose
 * figures a reader has already seen.
 *
 * The gate is unchanged: `pinProjectCode` is set only for the owner (`mayPinGoverningProforma`), and
 * the server refuses anyone else regardless. A non-owner still sees the disclosure.
 */
export function StoppedRunGoverningProforma({
  spine,
  pinProjectCode,
}: {
  spine?: ProformaSpineRead;
  /** Set only for the project OWNER (`basis:lock`), who alone may pin the governing proforma. */
  pinProjectCode?: string;
}) {
  if (!spine?.filename) return null;
  const rivals = spine.rivalsDisagree ?? [];
  const others = spine.alsoPresent ?? [];
  const pinUnusable = spine.governing !== undefined && !spine.governing.applied;
  /*
    The question is open when there is more than one template, OR when a pin on the record could not
    govern. ⚠️ The second holds even with ONE template: `applySpineOverride` withholds the override on
    `governing-pin-unavailable` regardless of rivals, so a stale or withdrawn pin over a lone remaining
    workbook stops the run too — and returning nothing there is #1395 again, one template smaller.
  */
  if (rivals.length === 0 && others.length === 0 && !pinUnusable) return null;
  /*
    Every workbook a person could name as governing, each once: the one the spine read, the others
    that parsed, and any rival not already listed.

    ⚠️ KEYED BY DOCUMENT ID, NEVER BY FILENAME. One project can hold several documents under the SAME
    filename with different ids (The Commons holds three) — keying by name would collapse them and
    leave all but one unpinnable. The filename is the key only for an entry with no id at all.
  */
  const keyOf = (c: { filename: string; documentId?: string }) =>
    c.documentId !== undefined ? `id:${c.documentId}` : `name:${c.filename}`;
  const candidates: {
    filename: string;
    documentId?: string;
    ingestedAt?: string;
    read: boolean;
    governs: boolean;
  }[] = [];
  const seen = new Set<string>();
  for (const [i, c] of [
    { filename: spine.filename, ...(spine.documentId ? { documentId: spine.documentId } : {}) },
    ...others,
    ...rivals,
  ].entries()) {
    const key = keyOf(c);
    if (seen.has(key)) continue;
    seen.add(key);
    candidates.push({
      filename: c.filename,
      ...(c.documentId ? { documentId: c.documentId } : {}),
      ...('ingestedAt' in c && c.ingestedAt ? { ingestedAt: c.ingestedAt } : {}),
      read: i === 0,
      /*
        ⚠️ THE FILE READ IS THE FILE PINNED, when the pin applied — compared by POSITION, not by id.
        `governing.documentId` is the id the pin NAMES; candidate ids are the LIVE ones, and a pin that
        governs through an alias (a supersede followed) names an id no candidate carries. The worker
        reports `applied` only when the read workbook IS the pinned one (`read === chosen` in
        `proforma-spine-shadow.ts`), so the spine's own entry — always first — is the governing file.
      */
      governs: i === 0 && spine.governing?.applied === true,
    });
  }
  const pinnable = candidates.filter((c) => !c.governs && c.documentId !== undefined);
  return (
    <section className="extract" aria-label="Governing proforma">
      <div className="extract__head">
        <span className="extract__badge">Governing proforma</span>
      </div>
      {rivals.length > 0 && (
        <p className="extract__gaps">
          The proforma workbooks in this project disagree on {disagreedFieldLabels(spine)}
          {spine.governing?.applied
            ? '. The project record names the governing proforma.'
            : ', so no workbook figure was applied to this run. Which file governs is a question for the project team.'}
        </p>
      )}
      {pinUnusable && (
        <p className="extract__gaps">
          <strong>The governing proforma pinned on the record could not be used</strong> (
          {GOVERNING_REASON_COPY[spine.governing?.reason ?? 'not-read']}).
        </p>
      )}
      <ul className="prov">
        {candidates.map((c) => (
          <li className="prov__row" key={keyOf(c)}>
            <span className="prov__field">{c.filename}</span>
            {/*
              ⚠️ SAME-NAME FILES MUST NOT LOOK IDENTICAL (Bugbot, #1401). The Commons holds three
              documents named `Copy of …JB_v1.4.xlsm`; three rows reading the same name give the owner
              three buttons and no way to choose. The date each was filed is what tells them apart —
              the same fact `DeterministicRead` shows — and the file this run read is said as such.
            */}
            <span className="prov__src">
              {c.read
                ? 'the file this run read'
                : c.ingestedAt
                  ? `added ${c.ingestedAt.slice(0, 10)}`
                  : 'date not recorded'}
            </span>
            {c.governs ? (
              <span className="prov__src">named on the record as governing</span>
            ) : (
              pinProjectCode &&
              c.documentId && (
                <GoverningPinAction
                  projectCode={pinProjectCode}
                  documentId={c.documentId}
                  filename={c.filename}
                />
              )
            )}
          </li>
        ))}
      </ul>
      {pinProjectCode && spine.governing && (
        <p className="extract__gaps">
          <GoverningPinAction
            projectCode={pinProjectCode}
            documentId={null}
            filename={spine.governing.filename ?? spine.filename}
          />
        </p>
      )}
      {/*
        This run stopped and will not be re-scored by a pin; say so before the reader expects it. Only
        when there is a pin to set or release — a note about acting on nothing clickable (a run written
        before N32 carries no document ids) is instructions with no control.
      */}
      {(pinnable.length > 0 || spine.governing) && (
        <p className="card__note">
          A governing-proforma pin applies from the next run — this run has stopped, so run the
          underwrite again after pinning.
        </p>
      )}
    </section>
  );
}

/** A compact summary of what extraction pulled from the document package (analyzed runs). */
export function ExtractionBanner({
  extraction,
  docCount,
  pinProjectCode,
}: {
  extraction: ExtractionSummary;
  docCount: number;
  pinProjectCode?: string;
}) {
  const prov = extraction.provenance ?? [];
  const overridden = new Set((extraction.spineOverride ?? []).map((o) => o.field));
  // ADR-0069 N27 — a field the RECORD pinned is superseded too, and by something stronger than the
  // workbook. The record is applied last, so it wins the label when both moved the same field.
  const pinnedByRecord = new Set((extraction.recordPins ?? []).map((p) => p.field as string));
  return (
    <section className="extract">
      <div className="extract__head">
        <span className="extract__badge">Extracted from documents</span>
        <span className="extract__meta">
          {docCount > 0 && `${docCount} document${docCount === 1 ? '' : 's'} · `}
          {extraction.fieldCount ?? prov.length} fields
          {extraction.model ? ` · ${extraction.model}` : ''}
        </span>
      </div>
      {extraction.narrative && <p className="extract__prose">{extraction.narrative}</p>}
      <DeterministicRead
        spine={extraction.spine}
        overrides={extraction.spineOverride}
        {...(pinProjectCode ? { pinProjectCode } : {})}
      />
      {extraction.gaps && extraction.gaps.length > 0 && (
        <p className="extract__gaps">
          Not stated in the package (soft gaps): {extraction.gaps.map(gapLabel).join(', ')}.
        </p>
      )}
      {prov.length > 0 && (
        <details className="extract__details">
          {/*
            ⚠️ THIS LIST IS THE MODEL'S READING, AND ON AN OVERRIDDEN FIELD IT IS NOT WHERE THE
            SCORED FIGURE CAME FROM. `extractionSummary` keeps the model's provenance verbatim so the
            record holds both sides of a disagreement — which is right for the record, and actively
            misleading on a page that renders the OVERRIDDEN figures in its metric tiles. Saying
            "Rent Roll.pdf · high" beside a number actually read from `OperatingPF!F33` is a worse
            failure than saying nothing, because it is confidently wrong rather than merely absent.
            The marker resolves it in place; the block above carries the cell.
          */}
          <summary>Field provenance ({prov.length})</summary>
          <ul className="prov">
            {prov.map((p, i) => {
              const superseded = overridden.has(p.field);
              const recordPinned = pinnedByRecord.has(p.field);
              return (
                <li className="prov__row" key={`${p.field}-${i}`}>
                  <span className="prov__field">{gapLabel(p.field)}</span>
                  <span className="prov__src">{p.sourceDoc ?? '—'}</span>
                  {p.confidence && (
                    <span className={`pill pill--badge pill--${p.confidence}`}>{p.confidence}</span>
                  )}
                  {recordPinned ? (
                    <span className="prov__superseded">superseded by the record</span>
                  ) : (
                    superseded && (
                      <span className="prov__superseded">superseded by the workbook</span>
                    )
                  )}
                </li>
              );
            })}
          </ul>
        </details>
      )}
    </section>
  );
}

/** Shown when the package was too thin to underwrite — name the missing run-blocking inputs. */
function NeedsMoreDocs({ result }: { result: IngestionResult }) {
  const gaps = result.runBlockingGaps ?? [];
  const ex = result.extraction;
  const spine = ex?.spine;
  /*
    ⚠️ "ADD A PROFORMA" IS THE WRONG ADVICE WHEN THE PROFORMAS ARE THE REASON (#1395). Rival templates
    that disagree on a run-blocking figure — or a governing pin that could not govern — withhold every
    workbook figure, so the run stops on a figure the proformas DO state. An owner who follows "add a
    proforma that states these figures" uploads a third rival and makes the question harder. Say what
    actually stopped the run, and point at the section below that answers it.
  */
  const disagreed = new Set(spine?.rivalsDisagree?.flatMap((r) => r.fields) ?? []);
  const rivalsBlock = !spine?.governing?.applied && gaps.some((g) => disagreed.has(g));
  const pinUnusable = spine?.governing !== undefined && !spine.governing.applied;
  const governingStopped = rivalsBlock || pinUnusable;
  return (
    <section className="card needsmore">
      <h3 className="card__title">More documents needed</h3>
      {governingStopped ? (
        <p className="card__prose">
          We ingested and read the package, but no workbook figure could be taken from it — so we
          stopped rather than score the deal on missing numbers.
        </p>
      ) : (
        <p className="card__prose">
          We ingested and read the package, but it didn&rsquo;t state the inputs required to
          underwrite the deal — so we stopped rather than score it on missing numbers.
        </p>
      )}
      {gaps.length > 0 && (
        <div className="needsmore__gaps">
          <span className="needsmore__label">
            {governingStopped ? 'Required, not taken from the documents:' : 'Required, not found:'}
          </span>
          <ul>
            {gaps.map((g) => (
              <li key={g}>{gapLabel(g)}</li>
            ))}
          </ul>
        </div>
      )}
      {ex && (ex.fieldCount ?? 0) > 0 && (
        <p className="card__note">
          {ex.fieldCount} other field{ex.fieldCount === 1 ? '' : 's'} were extracted
          {ex.narrative ? `: ${ex.narrative}` : '.'}
        </p>
      )}
      {rivalsBlock && spine ? (
        <p className="card__note">
          The proformas in this project disagree on {disagreedFieldLabels(spine)} — name the
          governing one below, then run it again.
        </p>
      ) : pinUnusable ? (
        <p className="card__note">
          The governing proforma pinned on the record could not be used — name the governing one
          below, or release the pin, then run it again.
        </p>
      ) : (
        <p className="card__note">
          Add a proforma or rent roll that states these figures and run it again.
        </p>
      )}
    </section>
  );
}

/**
 * The premium pipeline as the user experiences it. We only get two truthful run signals (queued →
 * running → succeeded/failed), so the stepper marks "Queued" done the moment a runner picks the job
 * up and treats everything after as the in-flight "Underwriting" stage — it never claims a sub-stage
 * (engine vs. model judging) is complete when we can't observe it. The third node lights only when
 * the run actually succeeds (at which point this view has already swapped to the report).
 */
const STAGES = [
  { key: 'queued', label: 'Queued', hint: 'Waiting for a runner' },
  { key: 'running', label: 'Underwriting', hint: 'Engine metrics, then the model judges' },
  { key: 'report', label: 'Report', hint: 'IC-memo-grade bundle' },
] as const;

function Progress({ run }: { run?: AnalysisRun }) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const started = Date.now();
    const t = setInterval(() => setElapsed(Math.floor((Date.now() - started) / 1000)), 1000);
    return () => clearInterval(t);
  }, []);

  // 0 = queued (or initial load), 1 = running. The report node (2) is never the active index here —
  // a succeeded run leaves this component entirely.
  const active = run?.status === 'running' ? 1 : 0;

  // The phase line under the bar. `awaiting_index` is an ingestion run waiting on Nexus to finish
  // OCR/embedding — distinct from a plain queue wait, so it must not read "waiting for a runner".
  //
  // #1400 — when the worker has reported a stage, the line names it and its place in the order
  // ("Step 3 of 5 — Reading the proforma workbook…") instead. Still no percentage: the steps are
  // nowhere near equal in length. No stage, or one this build does not know → the status line, as before.
  const steps = progressSteps(run?.progress?.stage);
  const currentAt = steps?.findIndex((s) => s.state === 'current') ?? -1;
  const current = steps?.[currentAt];
  const phase = current
    ? `Step ${String(currentAt + 1)} of ${String(steps?.length ?? 0)} — ${current.label}…`
    : run?.status === 'running'
      ? 'Running the model-judged analysis…'
      : run?.status === 'awaiting_index'
        ? 'Ingesting your documents — waiting for indexing…'
        : 'Queued — waiting for a runner…';

  return (
    <div className="progress">
      <ol className="stepper" aria-label="Analysis progress">
        {STAGES.map((s, i) => {
          const state = i < active ? 'done' : i === active ? 'active' : 'todo';
          return (
            <li key={s.key} className={`step step--${state}`}>
              <span className="step__dot" aria-hidden="true">
                {state === 'done' ? '✓' : i + 1}
              </span>
              <span className="step__body">
                <span className="step__label">{s.label}</span>
                <span className="step__hint">{s.hint}</span>
              </span>
            </li>
          );
        })}
      </ol>
      <div className="progress__bar" role="progressbar" aria-label="Working">
        <div className="progress__bar-indeterminate" />
      </div>
      <p className="progress__phase">
        {phase}
        <span className="progress__elapsed"> · {elapsed}s</span>
      </p>
      <p className="progress__hint">
        The premium run computes the deterministic engine bundle, then the model judges the
        Scorecard, Sensitivity, Lender, Sponsor, and IC memo. This usually takes under a minute.
      </p>
    </div>
  );
}
